/**
 * Centralized logout composable
 * Ensures proper cleanup of all data and states
 */

import { useToast } from '@/composables/useToast'
import { useLogoutMutation } from '@/queries/useAuth'
import { useSecureTempStorage } from '@/composables/useSecureTempStorage'
import { ValidationMessages } from '@/utils/validation'
import { queryClient } from '@/lib/vue-query'
import { useAutoCleanup, createQueryKey } from '@/lib/query-helpers'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { useUnregUserStore } from '@/stores/unregUserStore'
import { useWalletStore } from '@/stores/walletStore'
import { useRouter } from 'vue-router'

/**
 * Session storage keys to prevent race conditions
 */
const LOGOUT_IN_PROGRESS_KEY = 'logout_in_progress'
const LAST_LOGOUT_TIME_KEY = 'last_logout_time'

/**
 * Cookie management utilities
 */
const cookieUtils = {
  /**
   * Get all cookie names
   */
  getAllCookieNames(): string[] {
    return document.cookie
      .split(';')
      .map((cookie) => cookie.split('=')[0].trim())
      .filter((name) => name.length > 0)
  },

  /**
   * Clear cookie for all possible paths and domains
   */
  clearCookie(name: string) {
    const paths = ['/', '/api', '/app']
    const hostname = window.location.hostname
    const domains = [
      '',
      hostname,
      `.${hostname}`,
      // Add parent domains for subdomains
      ...hostname.split('.').reduce((acc: string[], _, index, arr) => {
        if (index > 0) {
          acc.push(`.${arr.slice(index).join('.')}`)
        }
        return acc
      }, [])
    ]

    // Clear for all combinations of paths and domains
    paths.forEach((path) => {
      domains.forEach((domain) => {
        const domainPart = domain ? `;domain=${domain}` : ''
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart}`
      })
    })
  },

  /**
   * Clear all cookies
   */
  clearAllCookies() {
    const cookieNames = this.getAllCookieNames()
    cookieNames.forEach((name) => this.clearCookie(name))
  }
}

/**
 * Storage management utilities
 */
const storageUtils = {
  /**
   * Safe localStorage cleanup
   */
  clearLocalStorage() {
    try {
      // Preserve important system data (languages, themes, etc.)
      const preserve = {
        language: localStorage.getItem('language'),
        theme: localStorage.getItem('theme'),
        preferences: localStorage.getItem('preferences')
      }

      localStorage.clear()

      // Restore preserved data
      Object.entries(preserve).forEach(([key, value]) => {
        if (value !== null) {
          localStorage.setItem(key, value)
        }
      })
    } catch (error) {
      console.warn('Failed to clear localStorage:', error)
    }
  },

  /**
   * Safe sessionStorage cleanup
   */
  clearSessionStorage() {
    try {
      sessionStorage.clear()
    } catch (error) {
      console.warn('Failed to clear sessionStorage:', error)
    }
  },

  /**
   * Set logout timestamp
   */
  setLogoutTimestamp() {
    try {
      sessionStorage.setItem(LAST_LOGOUT_TIME_KEY, Date.now().toString())
    } catch (error) {
      console.warn('Failed to set logout timestamp:', error)
    }
  },

  /**
   * Check if there was a recent logout (within 30 seconds)
   */
  hasRecentLogout(): boolean {
    try {
      const lastLogout = sessionStorage.getItem(LAST_LOGOUT_TIME_KEY)
      if (!lastLogout) return false

      const thirtySecondsAgo = Date.now() - 30 * 1000
      return parseInt(lastLogout, 10) > thirtySecondsAgo
    } catch (error) {
      return false
    }
  }
}

/**
 * Store cleanup manager
 */
const storeManager = {
  /**
   * Clear all Pinia stores
   */
  clearAllStores() {
    try {
      const authStore = useAuthStore()
      const walletStore = useWalletStore()
      const unregUserStore = useUnregUserStore()
      const uiStore = useUIStore()


      authStore.resetAuthState()
      authStore.clearActiveSessionMarker() // Clear session marker on logout
      walletStore.resetState()
      unregUserStore.reset()
      uiStore.resetUI()
    } catch (error) {
      console.warn('Failed to clear stores:', error)
    }
  },

  /**
   * Clear Vue Query cache with automatic cleanup
   */
  clearQueryCache() {
    try {
      // Define query keys that need to be cleared after logout
      const userRelatedQueries = [
        createQueryKey.user.info(),
        createQueryKey.user.overview(),
        createQueryKey.wallet.settings(),
        createQueryKey.auth.generate2fa()
      ]

      // Use useAutoCleanup for automatic cleanup after 5 minutes
      const { cleanup } = useAutoCleanup(userRelatedQueries, 5 * 60 * 1000)

      
      queryClient.clear()
      queryClient.cancelQueries()

      // Schedule additional cleanup in case something remains
      cleanup()
    } catch (error) {
      console.warn('Failed to clear query cache:', error)
    }
  }
}

/**
 * Main logout composable
 */
export function useLogout() {
  const router = useRouter()
  const { showSuccess, showError } = useToast()
  const logoutMutation = useLogoutMutation()

  /**
   * Check if logout is currently in progress
   */
  function isLogoutInProgress(): boolean {
    try {
      return sessionStorage.getItem(LOGOUT_IN_PROGRESS_KEY) === 'true'
    } catch {
      return false
    }
  }

  /**
   * Set logout progress flag
   */
  function setLogoutInProgress(inProgress: boolean) {
    try {
      if (inProgress) {
        sessionStorage.setItem(LOGOUT_IN_PROGRESS_KEY, 'true')
      } else {
        sessionStorage.removeItem(LOGOUT_IN_PROGRESS_KEY)
      }
    } catch (error) {
      console.warn('Failed to set logout progress flag:', error)
    }
  }

  /**
   * Perform API logout using Vue Query mutation
   */
  async function performApiLogout(): Promise<boolean> {
    try {
      await logoutMutation.mutateAsync()
      return true
    } catch (error) {
      console.warn('API logout failed, but continuing with local cleanup:', error)
      return false
    }
  }

  /**
   * Perform local data cleanup
   */
  function performLocalCleanup() {
    // Clear temporary auth storage
    const { clearAllTempData } = useSecureTempStorage()
    clearAllTempData()

  
    storeManager.clearAllStores()

    // Clear Vue Query cache
    storeManager.clearQueryCache()

    // Clear cookies
    cookieUtils.clearAllCookies()

    // Clear localStorage/sessionStorage
    storageUtils.clearLocalStorage()
    storageUtils.clearSessionStorage()

    // Set logout timestamp
    storageUtils.setLogoutTimestamp()
  }

  /**
   * Perform redirect after logout
   */
  function performRedirect() {
    // Clear current route
    router.replace({ name: 'login', query: {} })
  }

  /**
   * Main logout function
   */
  async function logout(showNotification = true): Promise<void> {
    // Prevent multiple logout calls
    if (isLogoutInProgress()) {
      console.warn('Logout already in progress')
      return
    }

    setLogoutInProgress(true)

    try {
      // 1. Perform API logout (not critical if it fails)
      const apiSuccess = await performApiLogout()

      // 2. Perform local cleanup (always)
      performLocalCleanup()

      // 3. Perform redirect
      performRedirect()

      // 4. Show notification
      if (showNotification) {
        if (apiSuccess) {
          showSuccess(ValidationMessages.SUCCESS_LOGOUT)
        } else {
          showSuccess(ValidationMessages.SUCCESS_LOGOUT_LOCAL)
        }
      }
    } catch (error) {
      console.error('Logout error:', error)

      // Even on error, perform local cleanup
      performLocalCleanup()
      performRedirect()

      if (showNotification) {
        showError(ValidationMessages.ERROR_LOGOUT)
      }
    } finally {
      setLogoutInProgress(false)
    }
  }

  /**
   * Force logout without API call (for expired sessions, etc.)
   */
  function forceLogout(reason?: string): void {
    console.trace('forceLogout call stack')

    if (isLogoutInProgress()) {
      return
    }

    setLogoutInProgress(true)

    try {
      performLocalCleanup()
      performRedirect()

      const message = reason ? `Session ended: ${reason}` : 'Session ended'
      showError(message)
    } finally {
      setLogoutInProgress(false)
    }
  }

  /**
   * Check if automatic authentication should be prevented
   */
  function shouldPreventAutoAuth(): boolean {
    return storageUtils.hasRecentLogout()
  }

  return {
    logout,
    forceLogout,
    isLogoutInProgress,
    shouldPreventAutoAuth
  }
}
